import React, {
  useEffect, useState, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import { DialogsProvider, ToastProvider } from '../../providers';
import { withContextProvider } from '../../hoc';
import HeaderWithBreadcrumbs from '../../componentsV2/Headers/HeaderWithBreadcrumbs';
import {
  createSite, updateSite, formatSiteData, useFetchSite, addImageToSite,
  siteHeader,
} from '../../utils/api/site';
import { addSitesToTag } from '../../utils/api/tag';
import { redirectToSite } from '../../utils/navigation';
import { useToasts, useCustomFieldErrorScroll } from '../../hooks';
import SiteFormWrapper from '../../componentsV2/Site/SiteCreateEditForm/SiteFormWrapper';
import { capitalizeFirstLetter } from '../../utils/api/index';
import { statusOptions, handleNameValidation } from '../../utils/siteForm';
import { useLocalStorage } from '../../hooks/useLocalStorage';

const DEFAULT_ORG_CONTACTS = [];

const SiteForm = ({
  siteId = null,
  organization,
  organizationTags,
  orgContacts = DEFAULT_ORG_CONTACTS,
  initialModelFormItems = [],
}) => {
  const initialState = {
    name: '',
    organization_id: organization.id,
    status: 'Active',
    alternate_ids: '',
    alternate_names: '',
    description: '',
    units: '',
    worker_count: '',
    arrival: '',
    departure: '',
    h2a: null,
    icon: '',
    address1: '',
    address2: '',
    city: '',
    county: '',
    state: '',
    zip_code: '',
    region: '',
    coords_set_manually: false,
    latitude: null,
    longitude: null,
    directions: '',
    memo: '',
    site_type: '',
    primary_contact_id: '',
    contact_ids: [],
    custom_fields: initialModelFormItems
      ? initialModelFormItems.map((item, index) => {
        return {
          key: item.key,
          type: item.type,
          label: item.label,
          position: index + 1,
          value: null,
          form_item_id: item.key,
          values: item.values,
          required: item.required,
        };
      })
      : [],
  };

  // first element is not used, but we need comma to destructure the rest of the array
  const { setToLocalStorage, getFromLocalStorage, removeFromLocalStorage } = useLocalStorage();
  const [siteData, setSiteData] = useState(initialState);
  const [initialSiteData, setInitialSiteData] = useState(initialState);
  const [assignedTags, setAssignedTags] = useState([]);
  const [errorMessage, setErrorMessage] = useState({ name: '', state: '', worker_count: '' });
  const [primaryContactName, setPrimaryContactName] = useState('');
  const [currentSiteImages, setCurrentSiteImages] = useState([]);
  const [siteName, setSiteName] = useState('');
  const [hasCustomFieldsChanged, setHasCustomFieldsChanged] = useState(false);
  const [isSendingForm, setIsSendingForm] = useState(false);
  const [validationMessage, setValidationMessage] = useState({ name: '' });
  const [siteNameFromInput, setSiteNameFromInput] = useState('');
  const [isUnsavedChanges, setIsUnsavedChanges] = useState(false);
  const [shouldScrollToError, setShouldScrollToError] = useState(false);
  const triggerCustomFieldErrorScroll = useCustomFieldErrorScroll(setShouldScrollToError);

  const hasUnsavedChanges = !isEqual(siteData, initialSiteData) || hasCustomFieldsChanged;

  const { showToast } = useToasts();
  const siteForEdit = useMemo(() => {
    try {
      return getFromLocalStorage('siteForEdit');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      return null;
    }
  }, [getFromLocalStorage]);
  const fetchSite = useFetchSite({
    siteId, organizationId: organization.id, setSiteData, setInitialSiteData, showToast, setAssignedTags, setPrimaryContactName, setSiteName,
  });

  // set the site data for add or edit form
  useEffect(() => {
    if (!siteId) {
      return;
    }
    if (siteForEdit) {
      const processedCustomFields = siteForEdit.custom_fields.map((field) => {
        if (field.type === 'checkbox-group') {
          const selected = field.values?.filter(opt => opt.selected).map(opt => opt.value) || null;
          return { ...field, value: selected };
        }
        if (field.type === 'select') {
          const selectedOption = field.values?.find(opt => opt.selected)?.value ?? null;
          return { ...field, value: selectedOption };
        }
        return field;
      });

      setSiteData({
        ...siteForEdit,
        status: capitalizeFirstLetter(siteForEdit?.status),
        custom_fields: processedCustomFields,

      });
      setInitialSiteData({
        ...siteForEdit,
        status: capitalizeFirstLetter(siteForEdit?.status),
        custom_fields: processedCustomFields,

      });
      setSiteName(siteForEdit.name);
      // assign tags to display on the add/edit site form
      if (siteForEdit.tags) {
        setAssignedTags(siteForEdit.tags);
      }
      // set primary contact name to display on the add/edit site form
      if (siteForEdit.primary_contact_id) {
        const contact = siteForEdit.contacts?.find(({ id }) => id === siteForEdit.primary_contact_id);
        setPrimaryContactName(contact?.name);
      }
    } else {
      fetchSite();
    }

    removeFromLocalStorage('siteForEdit');
  }, [fetchSite, siteForEdit, siteId, removeFromLocalStorage]);

  useEffect(() => {
    setIsUnsavedChanges(hasUnsavedChanges);
  }, [hasUnsavedChanges, siteData, hasCustomFieldsChanged]); // hasCustomFieldsChanged, siteData - track changes to siteData, hasCustomFieldsChanged for unsaved changes if isUnsavedChanges was set to false on previous changes

  const validateRequiredFields = (data) => {
    const errors = {};
    if (data.name === '') {
      errors.name = 'Site name is required';
    }
    if (data.state === '') {
      errors.state = 'State is required';
    }

    data.custom_fields?.forEach((field) => {
      const isEmpty = Array.isArray(field.value)
        ? field.value.length === 0
        : !field.value;

      if (field.required && isEmpty) {
        errors[`custom_fields.${field.label}`] = `${field.label} is required`;
      }
    });
    if (data.worker_count < 0) {
      errors.worker_count = "Can't be negative";
    }
    return errors;
  };

  useEffect(() => {
    const errors = validateRequiredFields(siteData);
    setErrorMessage(errors);
  }, [siteData]);

  useEffect(() => {
    if (!isEqual(siteNameFromInput, siteData.name)) {
      setValidationMessage({ name: '' });
    }
  }, [siteNameFromInput, siteData.name]);

  useEffect(() => {
    if (siteData?.site_images) {
      setCurrentSiteImages(siteData.site_images);
    }
  }, [siteData]);

  const onSave = () => {
    // checking if required fields are filled
    const errors = validateRequiredFields(siteData);
    if (Object.keys(errors).length > 0) {
      setIsSendingForm(false);
      const hasTopLevelErrors = Object.keys(errors).some((key) => !key.startsWith('custom_fields.'));
      triggerCustomFieldErrorScroll(hasTopLevelErrors);
      return;
    }
    setIsSendingForm(true);

    siteData.custom_fields = siteData.custom_fields.map((field) => {
      if (field.type === 'checkbox-group' && Array.isArray(field.value)) {
        return {
          ...field,
          value: JSON.stringify(field.value),
        };
      }
      return field;
    });

    // modified data to send to the API, including status options and custom fields
    const formattedData = formatSiteData(siteData, statusOptions, initialState);
    const apiRequest = siteId ? updateSite : createSite;
    apiRequest(formattedData, organization.id, siteId)
      .then((response) => {
        if (!response.ok && response.status !== 422) {
          throw new Error(response.statusText);
        }
        return response.json()
          .then((data) => {
            return ({ data, status: response.status });
          });
      })
      .then((data) => {
        if (data.status === 422 && data.data.some((msg) => msg.includes('Name'))) {
          // if the error is about name attribute, show inline error message, otherwise show toast
          // filter out messages that are not related to the name attribute
          const toastMessage = data.data.filter((msg) => !msg.includes('Name'));
          if (toastMessage.length > 0) {
            showToast({ message: toastMessage.join(', '), type: 'error' });
          }
          const inlineErrorMessage = handleNameValidation(data);
          setValidationMessage({ name: inlineErrorMessage });
          setSiteNameFromInput(siteData.name);
          return;
        }
        if (data.status === 200 || data.status === 201) {
          const action = data.status === 201 ? 'created' : 'updated';
          showToast({ message: `Site ${data.data.site.name} has been ${action}`, type: 'success' });

          const hasCoordsChanged = (
            !isEqual(siteData.latitude, initialSiteData.latitude)
            || !isEqual(siteData.longitude, initialSiteData.longitude)
          );
          if (hasCoordsChanged) {
            // Clear first so the storage event fires even if the value was already 'true'
            // some browsers skip the event when setItem writes an identical value.
            removeFromLocalStorage('site-location-changed');
            setToLocalStorage('site-location-changed', 'true');
          }
        } else {
          throw new Error(data?.data?.error || data?.data?.message || data?.data || 'The site could not be saved');
        }
        // only if site is created, assign tags to the site
        if (!siteId && assignedTags.length > 0) {
          assignedTags.forEach((tag) => {
            addSitesToTag(tag.id, organization.id, [data.data.site.id])
              .then((response) => {
                if (!response.ok && response.status !== 422) {
                  throw new Error(response.statusText);
                }
                if (!response.ok && response.status === 422) {
                  return response.json()
                    .then((erroredData) => {
                      throw new Error(erroredData.error);
                    });
                }
                return null;
              })
              .catch((error) => {
                showToast({ message: error.message, type: 'error' });
              });
          });
        }
        // only if site is created, assign images to the site
        if (!siteId && currentSiteImages.length > 0) {
          Promise.all(
            currentSiteImages.map((unsavedImage) => {
              const siteImagePayload = {
                site_image: {},
                image: unsavedImage.image,
              };
              return addImageToSite(siteImagePayload, data.data.site.id, organization.id)
                .then((response) => {
                  if (!response.ok) {
                    throw new Error(response.statusText);
                  }
                  return response.json();
                });
            }),
          )
            .then(() => {
              showToast({ message: 'Image has been added.', type: 'success' });
            })
            .catch((error) => {
              showToast({ message: `Failed to add images: ${error.message}`, type: 'error' });
            });
        }
        setTimeout(() => {
          redirectToSite(data.data.site.id);
        }, 1000);
      })
      .catch((error) => {
        showToast({ message: error.message, type: 'error' });
      })
      .finally(() => {
        setIsSendingForm(false);
        setIsUnsavedChanges(false);
      });
  };

  const breadcrumbLinks = [
    {
      href: '/sites',
      name: 'Sites',
    },
    {
      name: siteId ? siteName : 'New Site',
    },
  ];

  return (
    <DialogsProvider>
      {/* alignment according the Mock Up */}
      <div className="ml-2.75 mr-10.5">
        <header>
          <HeaderWithBreadcrumbs links={breadcrumbLinks} className="pb-4" name="site" label="Sites" size="h-10" shouldNotDisplayHeader />
          {/* Using initial site data to prevent header from changing when typing. Assuming this is desired UX */}
          {siteHeader(initialSiteData)}
        </header>
        <main className="flex flex-col gap-y-7">
          <SiteFormWrapper
            onSave={onSave}
            siteData={siteData}
            setSiteData={setSiteData}
            orgContacts={orgContacts}
            organization={organization}
            assignedTags={assignedTags}
            setAssignedTags={setAssignedTags}
            errorMessage={errorMessage}
            primaryContactName={primaryContactName}
            setPrimaryContactName={setPrimaryContactName}
            organizationTags={organizationTags}
            currentSiteImages={currentSiteImages}
            setCurrentSiteImages={setCurrentSiteImages}
            setErrorMessage={setErrorMessage}
            isSendingForm={isSendingForm}
            setCustomFieldsChanged={setHasCustomFieldsChanged}
            validationMessage={validationMessage}
            isUnsavedChanges={isUnsavedChanges}
            setIsUnsavedChanges={setIsUnsavedChanges}
            hasUnsavedChanges={hasUnsavedChanges}
            shouldScrollToError={shouldScrollToError}
          />
        </main>
      </div>
    </DialogsProvider>
  );
};

SiteForm.propTypes = {
  siteId: PropTypes.number,
  organization: PropTypes.object.isRequired,
  organizationTags: PropTypes.array.isRequired,
  orgContacts: PropTypes.array,
  initialModelFormItems: PropTypes.array,
};

export default withContextProvider(ToastProvider)(SiteForm);
